import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useCallback, useMemo } from 'react';
import ItemModifierOptions from '../../containers/ItemModifierOptions/ItemModifierOptions';
import itemsStore from '../../store/itemsStore';
import { IItemModifierValidation } from '../../types/items';
import { LOCALES } from 'src/constants/main';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { TBooleanInNumber } from 'src/types/main';
import { IProductV4Modifier } from 'src/types/product';
import { booleanNumberReverse, getTranslate, isDisabledByPos } from 'src/utils/helpers';
import { Checkbox } from 'src/view/components/Controls/Checkbox/Checkbox';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Input from 'src/view/components/Controls/Input/Input';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import DeleteRestoreBlock from 'src/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import Text, { TEXT } from 'src/view/components/Text/Text';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './style.scss';

enum INPUTS_NAME {
  SORT = 'sort',
  MIN = 'min_options',
  MAX = 'max_options',
}

interface IProps {
  modifier: IProductV4Modifier;
}

const ItemModifier: React.FC<IProps> = observer((props: IProps): JSX.Element => {
  const inputDisabled: boolean = !props.modifier.multi_choice;
  const modifierValid: IItemModifierValidation | undefined = itemsStore.validation.modifiers.find((_modifier) => _modifier.id === props.modifier.id);
  const inputClassName = useMemo((): string => {
    let className: string = 'item-modifier__input family-semibold text-size-18';
    className += `${localeStore.language === LOCALES.EN ? ' text-end' : ' text-start'}`;

    return className;
  }, [inputDisabled]);

  const changeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value: string = event.target.value.replace(/\D/, '');
      const valueTNumber: number = Number(value);

      if (event.target.name === INPUTS_NAME.MIN) {
        itemsStore.updateModifier(props.modifier.id, { minimum: !value || isNaN(valueTNumber) ? undefined : valueTNumber });
        // update validation
        if (modifierValid && (!modifierValid?.minimum || !modifierValid.minMaxAccordance || !modifierValid.quantitySumm)) itemsStore.setValidation();
      } else if (event.target.name === INPUTS_NAME.MAX) {
        itemsStore.updateModifier(props.modifier.id, { maximum: !value || isNaN(valueTNumber) ? undefined : valueTNumber });
        // update validation
        if (modifierValid && (!modifierValid?.maximum || !modifierValid.minMaxAccordance)) itemsStore.setValidation();
      } else {
        itemsStore.updateModifier(props.modifier.id, { sort: !value || isNaN(valueTNumber) ? undefined : valueTNumber });
      }
    },
    [props.modifier.id, modifierValid]
  );

  const changeCheckboxUniqueOptions = useCallback(() => {
    itemsStore.updateModifierUniqueOptions(props.modifier.id, booleanNumberReverse(props.modifier.unique_options));
    // update validation
    if (modifierValid && !modifierValid?.quantitySumm) itemsStore.setValidation();
  }, [props.modifier.id, props.modifier.unique_options, modifierValid]);

  const changeSwitcherSingleChoise = useCallback(
    (status: TBooleanInNumber) => {
      itemsStore.updateModifierSingleChoise(props.modifier.id, booleanNumberReverse(status));
      // update validation
      if (modifierValid && (!modifierValid?.quantitySumm || modifierValid?.minMaxAccordance)) itemsStore.setValidation();
    },
    [props.modifier.id, modifierValid]
  );

  const renderInput = useCallback(
    (name: INPUTS_NAME, value: number | undefined, wrapperClassName: string, error?: boolean, required?: boolean): JSX.Element => {
      const disabled = name !== INPUTS_NAME.SORT && inputDisabled;
      return (
        <Input
          key={name}
          type="text"
          name={name}
          className={classNames(inputClassName, { 'cursor-not-allowed': disabled })}
          wrapperClassName={classNames('mr-2', wrapperClassName, { disabled })}
          // space required !!!
          value={value || value === 0 ? value.toString() : ' '}
          onInput={changeInput}
          error={modifierValid ? error : undefined}
          errorMessage={localeStore.t('required_field_error')}
          disabled={disabled}
        >
          <p className={`item-modifier__input-text family-medium color-dark text-size-16 ${required ? 'required' : ''}`}>{localeStore.t(name)}</p>
        </Input>
      );
    },
    [changeInput, modifierValid, inputClassName, inputDisabled]
  );

  const { modifier } = props;

  return (
    <li className="item-modifier">
      <div className="d-flex flex-row align-items-center justify-content-between">
        <p className="family-semibold text-size-20 color-dark">{getTranslate(modifier.name)}</p>
        <IntegrationManager setting={integrationsStore.posRestrictions?.deleteModifierFromItem} withTooltip>
          <DeleteRestoreBlock iconSize={20} iconClassName="color-dark" onDelete={() => itemsStore.changeModifiers(modifier.id)} />
        </IntegrationManager>
      </div>
      <div className="d-flex flex-row mt-4">
        {renderInput(INPUTS_NAME.SORT, modifier.sort, 'item-modifier__input-sort-wrapper')}
        <IntegrationManager setting={integrationsStore.posRestrictions?.itemModifierMin} withTooltip>
          {renderInput(INPUTS_NAME.MIN, modifier.minimum, 'item-modifier__input-quantity-wrapper', !modifierValid?.minimum, true)}
        </IntegrationManager>
        <IntegrationManager setting={integrationsStore.posRestrictions?.itemModifierMax} withTooltip>
          {renderInput(INPUTS_NAME.MAX, modifier.maximum, 'item-modifier__input-quantity-wrapper', !modifierValid?.maximum, true)}
        </IntegrationManager>
        <div className="item-modifier__no-validation-block-wrapper">
          <div className="d-flex flex-row align-items-center">
            <IntegrationManager setting={integrationsStore.posRestrictions?.uniqueOptionsAvailability} withTooltip>
              <Checkbox
                value={Boolean(modifier.unique_options)}
                name="unique_option"
                label="unique_option"
                className="mr-3"
                disabled={inputDisabled}
                onClick={changeCheckboxUniqueOptions}
              />
            </IntegrationManager>
            <TooltipIcon message="unique_option_hint" duration={TOOLTIP_DURATION.EXTRA_LARGE} maxWidth={295} />
          </div>
          <div className="d-flex flex-row align-items-center">
            <IntegrationManager setting={integrationsStore.posRestrictions?.singleChoice} withTooltip>
              <Switcher status={booleanNumberReverse(modifier.multi_choice)} onChange={changeSwitcherSingleChoise} />
            </IntegrationManager>
            <Text
              type={TEXT.SWITCHER_LABEL}
              text="single_choice"
              disabled={isDisabledByPos(integrationsStore.posRestrictions?.singleChoice)}
              tooltip={{
                message: 'single_choice_hint',
                position: TOOLTIP_POSITION.FIXED,
                duration: TOOLTIP_DURATION.EXTRA_LARGE,
                maxWidth: 295,
                iconSize: 16,
              }}
            />
          </div>
        </div>
      </div>
      <ItemModifierOptions modifier={modifier} modifierValid={modifierValid} options={modifier.options} />
    </li>
  );
});

export default ItemModifier;
